import { TimeRange } from "@/interfaces/Availability";
import type {
  CsmAvailability,
  MontageBusySlot,
} from "@/services/montageMeetings";
import { toMinutes } from "./availability";

const hhmm = (minute: number) =>
  `${String(Math.floor(minute / 60)).padStart(2, "0")}:${String(
    minute % 60,
  ).padStart(2, "0")}`;
export function buildMeetingSlots(
  ranges: TimeRange[],
  stepMinutes: number,
  durationMinutes = stepMinutes,
): TimeRange[] {
  if (
    [stepMinutes, durationMinutes].some(
      (value) =>
        !Number.isInteger(value) ||
        value < 15 ||
        value > 180 ||
        (value !== 20 && value % 15 !== 0),
    )
  )
    return [];
  return ranges.flatMap((range) => {
    const slots: TimeRange[] = [];
    for (
      let start = toMinutes(range.start);
      start + durationMinutes <= toMinutes(range.end);
      start += stepMinutes
    ) {
      slots.push({ start: hhmm(start), end: hhmm(start + durationMinutes) });
    }
    return slots;
  });
}
export function busySlotFor(
  entry: CsmAvailability | undefined,
  hour: string,
  durationMinutes = entry?.slotMinutes ?? 60,
  excludeMeetingId?: string,
): MontageBusySlot | undefined {
  const start = toMinutes(hour);
  return entry?.busySlots.find((busy) => {
    if (busy.meetingId === excludeMeetingId) return false;
    const otherStart = toMinutes(busy.start ?? busy.hour);
    const otherEnd = busy.end ? toMinutes(busy.end) : otherStart + 60;
    return otherStart < start + durationMinutes && otherEnd > start;
  });
}
export function isSelectableSlot(
  entry: CsmAvailability | undefined,
  hour: string,
  options: {
    durationMinutes?: number;
    excludeMeetingId?: string;
    allowBusy?: boolean;
    allowUnavailable?: boolean;
  } = {},
): boolean {
  if (!entry) return false;
  const duration = options.durationMinutes ?? entry.slotMinutes ?? 60;
  if (
    !options.allowUnavailable &&
    (entry.unavailableReason ||
      !buildMeetingSlots(
        entry.ranges ?? [],
        entry.slotMinutes ?? 60,
        duration,
      ).some((slot) => slot.start === hour))
  )
    return false;
  return (
    !!options.allowBusy ||
    !busySlotFor(entry, hour, duration, options.excludeMeetingId)
  );
}
